import { useState } from 'react';
import { Check, Loader2, Send, Users } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getAvatarUrl } from '@/lib/avatars';
import { useAuth } from '@/hooks/useAuth';
import { useSharedItems } from '@/hooks/useSharedItems';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface ShareProduct {
  name: string;
  image_url: string;
  url?: string;
  price?: number | null;
  currency?: string;
  store?: string;
}

interface InlineFriendPickerProps {
  savedItemId?: string;
  pollId?: string;
  lookId?: string;
  collectionId?: string;
  bodyGymLookId?: string;
  /** Raw product metadata — allows sharing without a savedItemId */
  product?: ShareProduct;
  itemName: string;
  onSent?: () => void;
}

interface Friend {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

export function InlineFriendPicker({
  savedItemId,
  pollId,
  lookId,
  collectionId,
  bodyGymLookId,
  product,
  itemName,
  onSent,
}: InlineFriendPickerProps) {
  const { user } = useAuth();
  const { shareItem } = useSharedItems();
  const [selectedFriends, setSelectedFriends] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);

  const { data: friends, isLoading } = useQuery({
    queryKey: ['mutual-friends', user?.id],
    queryFn: async () => {
      if (!user) return [];

      // Get users I follow
      const { data: followData, error: followError } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id);

      if (followError) throw followError;
      if (!followData || followData.length === 0) return [];

      const followingIds = followData.map(f => f.following_id);

      // Get which of those follow me back (mutual)
      const { data: mutualData, error: mutualError } = await supabase
        .from('follows')
        .select('follower_id')
        .eq('following_id', user.id)
        .in('follower_id', followingIds);

      if (mutualError) throw mutualError;
      if (!mutualData || mutualData.length === 0) return [];

      const mutualIds = mutualData.map(f => f.follower_id);

      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, user_id, display_name, avatar_url')
        .in('user_id', mutualIds);

      if (profileError) throw profileError;
      return profiles as Friend[];
    },
    enabled: !!user,
  });

  const toggleFriend = (userId: string) => {
    setSelectedFriends(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSend = async () => {
    if (selectedFriends.length === 0) return;

    setIsSending(true);
    try {
      if (product && !savedItemId && !pollId && !lookId && !collectionId && !bodyGymLookId) {
        // Share raw product metadata directly via edge function
        await Promise.all(
          selectedFriends.map(friendId =>
            supabase.functions.invoke('share-product-suggestion', {
              body: {
                recipient_id: friendId,
                product: {
                  name: product.name || 'Shared item',
                  image_url: product.image_url,
                  url: product.url || null,
                  price: product.price || null,
                  currency: product.currency || 'GBP',
                  store: product.store || null,
                },
              },
            }).then(({ error }) => { if (error) throw error; })
          )
        );
      } else if (savedItemId && !pollId && !lookId && !collectionId && !bodyGymLookId) {
        // Route saved-item shares through share-product-suggestion to copy the item
        const { data: itemRaw, error: fetchError } = await supabase
          .from('saved_items')
          .select('id, name, image_url, url, price, currency, store, page_id, flux_pages:page_id(url, tld, data)')
          .eq('id', savedItemId)
          .single();

        if (fetchError || !itemRaw) throw new Error('Could not load item details');

        const fp = (itemRaw as any).flux_pages;
        const d = fp?.data;
        const hasFluxData = (itemRaw as any).page_id && d && typeof d === 'object';
        const itemData = {
          name: hasFluxData ? (d.title || (itemRaw as any).name) : ((itemRaw as any).name ?? null),
          image_url: hasFluxData ? (d.cdnUrls?.[0] || d.imageUrls?.[0] || (itemRaw as any).image_url) : ((itemRaw as any).image_url ?? null),
          url: hasFluxData ? (fp.url || (itemRaw as any).url) : ((itemRaw as any).url ?? null),
          price: hasFluxData ? (d.price != null ? parseFloat(String(d.price)) : ((itemRaw as any).price ?? null)) : ((itemRaw as any).price ?? null),
          currency: hasFluxData ? (d.currency || (itemRaw as any).currency || 'GBP') : ((itemRaw as any).currency ?? 'GBP'),
          store: hasFluxData ? (fp.tld || d.brand || (itemRaw as any).store) : ((itemRaw as any).store ?? null),
        };

        await Promise.all(
          selectedFriends.map(friendId =>
            supabase.functions.invoke('share-product-suggestion', {
              body: {
                recipient_id: friendId,
                product: {
                  name: itemData.name || 'Shared item',
                  image_url: itemData.image_url,
                  url: itemData.url || null,
                  price: itemData.price || null,
                  currency: itemData.currency || 'GBP',
                  store: itemData.store || null,
                },
              },
            }).then(({ error }) => { if (error) throw error; })
          )
        );
      } else {
        // Polls, looks, collections use existing path
        await Promise.all(
          selectedFriends.map(friendId =>
            shareItem.mutateAsync({
              recipient_id: friendId,
              saved_item_id: savedItemId,
              poll_id: pollId,
              look_id: lookId,
              collection_id: collectionId,
              body_gym_look_id: bodyGymLookId,
            })
          )
        );
      }

      const itemType = collectionId ? 'Collection' : lookId ? 'Look' : pollId ? 'Poll' : 'Item';
      toast.success(
        selectedFriends.length === 1
          ? `${itemType} sent!`
          : `${itemType} sent to ${selectedFriends.length} friends!`
      );

      setSelectedFriends([]);
      onSent?.();
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Failed to send');
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!friends || friends.length === 0) {
    return (
      <div className="flex items-center gap-3 p-3 bg-surface-2 rounded-2xl">
        <Users className="w-5 h-5 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">You can only share with mutual friends - follow each other to unlock sharing</p>
      </div>
    );
  }

  return (
    <div>
      {/* Horizontal scrollable friend avatars */}
      <div className="flex gap-3 overflow-x-auto pt-1 pb-2 -mx-1 px-1 scrollbar-hide">
        {friends.map(friend => {
          const isSelected = selectedFriends.includes(friend.user_id);
          return (
            <button
              key={friend.user_id}
              onClick={() => toggleFriend(friend.user_id)}
              className="flex flex-col items-center gap-1.5 flex-shrink-0 w-16 active:scale-[0.95] transition-transform"
            >
              <div className="relative">
                <Avatar className={cn(
                  "w-12 h-12 transition-all",
                  isSelected && "ring-2 ring-primary ring-offset-2 ring-offset-background"
                )}>
                  <AvatarImage src={getAvatarUrl(friend.avatar_url, friend.user_id)} />
                  <AvatarFallback className="bg-muted" />
                </Avatar>
                {isSelected && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <Check className="w-3 h-3 text-primary-foreground" />
                  </div>
                )}
              </div>
              <span className="text-[11px] text-muted-foreground leading-tight text-center line-clamp-1 w-full">
                {friend.display_name?.split(' ')[0] || 'Friend'}
              </span>
            </button>
          );
        })}
      </div>

      {/* Send button - appears when friends selected */}
      {selectedFriends.length > 0 && (
        <button
          onClick={handleSend}
          disabled={isSending}
          className="w-full mt-3 flex items-center justify-center gap-2 h-11 rounded-full bg-primary text-primary-foreground font-medium text-sm active:scale-[0.98] transition-all disabled:opacity-50"
        >
          {isSending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {isSending
            ? 'Sending...'
            : `Send to ${selectedFriends.length} Friend${selectedFriends.length !== 1 ? 's' : ''}`
          }
        </button>
      )}
    </div>
  );
}
